import type { PaymentMilestoneData, ReplacingQuoteData } from '@/lib/api'
import {
  PAYLINK_STATUS,
  PaymentMilestoneStateCode,
  PaymentMilestoneStatus,
  QuoteStateCode,
  QuoteType,
} from '@/lib/types'
import { parseChangeOrderName } from '@/lib/utils'
import Decimal from 'decimal.js'

type ReservableMilestone = Pick<
  PaymentMilestoneData,
  | 'dsy_paymentmilestoneid'
  | 'dsy_amount'
  | 'statecode'
  | 'statuscode'
  | 'dsy_paylink'
  | '_dsy_replacedbyquote_value'
  | 'dsy_replacedbyquote'
  | '_dsy_epayment_value'
  | 'dsy_invoiceissueddate'
  | 'dsy_quickbooksinvoiceid'
  | 'dsy_quickbookslastsyncerror'
>

type ReplacementFields = Pick<
  PaymentMilestoneData,
  '_dsy_replacedbyquote_value' | 'dsy_replacedbyquote' | 'statecode' | 'statuscode'
>

/** `self`: the change order being viewed replaces this row (shown from the
 * moment it is reserved). `other`: an applied change order replaced it. */
export type MilestoneReplacement = { kind: 'self' } | { kind: 'other'; quoteId: string; label: string }

/** Customer-facing name of the replacing quote: "Change Order N". */
export function getReplacingQuoteLabel(quote: ReplacingQuoteData | null | undefined) {
  const changeOrder = quote?.name ? parseChangeOrderName(quote.name) : null
  if (changeOrder) return `Change Order ${changeOrder.number}`
  return quote?.quotenumber ?? 'a change order'
}

function isMilestoneInactive(milestone: Pick<PaymentMilestoneData, 'statecode' | 'statuscode'>) {
  return (
    milestone.statecode === PaymentMilestoneStateCode.Inactive ||
    milestone.statuscode === PaymentMilestoneStatus.Inactive
  )
}

// A lookup to a Closed (lost/cancelled) quote is void: the row is free and
// was never replaced.
function isHeldByAnotherQuote(milestone: ReplacementFields, quoteId: string) {
  const holder = milestone._dsy_replacedbyquote_value
  return !!holder && holder !== quoteId && milestone.dsy_replacedbyquote?.statecode !== QuoteStateCode.Closed
}

/** A row a PayLink can bill: active and not held by a change order that is
 * still open (a Closed holder's lookup is void). Mirrors the PayLink plug-in. */
export function isBillableMilestone(milestone: ReplacementFields) {
  return (
    !isMilestoneInactive(milestone) &&
    !(milestone._dsy_replacedbyquote_value && milestone.dsy_replacedbyquote?.statecode !== QuoteStateCode.Closed)
  )
}

// A reservation by a Draft/Active change order is only visible on that change
// order; every other proposal shows the row as replaced once the change order
// is applied (row Inactive with a live lookup).
export function getMilestoneReplacement(milestone: ReplacementFields, quoteId: string): MilestoneReplacement | null {
  const holder = milestone._dsy_replacedbyquote_value
  if (!holder) return null
  if (holder === quoteId) return { kind: 'self' }
  if (!isMilestoneInactive(milestone) || !isHeldByAnotherQuote(milestone, quoteId)) return null
  return { kind: 'other', quoteId: holder, label: getReplacingQuoteLabel(milestone.dsy_replacedbyquote) }
}

export function sumMilestoneAmounts(milestones: Pick<PaymentMilestoneData, 'dsy_amount'>[]) {
  return milestones
    .reduce((sum, milestone) => sum.add(milestone.dsy_amount), new Decimal(0))
    .toDecimalPlaces(2)
    .toNumber()
}

/** Sales-order milestones this change order replaces (reserved while Draft). */
export function getReservedMilestones<T extends Pick<PaymentMilestoneData, '_dsy_replacedbyquote_value'>>(
  salesOrderMilestones: T[] | undefined,
  quoteId: string | undefined
) {
  if (!quoteId) return []
  return (salesOrderMilestones ?? []).filter((milestone) => milestone._dsy_replacedbyquote_value === quoteId)
}

/** The amount a quote's own milestones must total. For a change order that is
 * its delta plus every milestone it replaces (mirrors proposal-api
 * functions/milestone_replacement.py); otherwise the quote total. */
export function getPaymentMilestonePool({
  quoteType,
  summaryTotal,
  reservedTotal,
}: {
  quoteType: QuoteType | number | undefined
  summaryTotal: number
  reservedTotal: number
}) {
  if (quoteType !== QuoteType.ChangeOrder) return summaryTotal
  return new Decimal(summaryTotal).add(reservedTotal).toDecimalPlaces(2).toNumber()
}

export const REPLACEMENT_BLOCKER = {
  paid: 'Paid',
  paymentReceived: 'Payment received',
  waitingForPayment: 'Waiting for payment',
  paymentLinkSent: 'Payment link sent',
  invoiced: 'Invoiced',
  quickBooksReview: 'QuickBooks invoice needs review',
  toBeInvoiced: 'To be invoiced',
  inactive: 'Inactive',
} as const

// Blocks staff can clear themselves (cancel the link / AR voids the invoice).
const ACTIONABLE_BLOCKERS = new Set<string>([
  REPLACEMENT_BLOCKER.waitingForPayment,
  REPLACEMENT_BLOCKER.paymentLinkSent,
  REPLACEMENT_BLOCKER.invoiced,
])

export function hasActionableReplacementBlocker(blockers: (string | null)[]) {
  return blockers.some((blocker) => !!blocker && ACTIONABLE_BLOCKERS.has(blocker))
}

/** The dialog's "Replace existing payments" disclosure starts open when the
 * change order already replaces (or the draft selects) a milestone. */
export function isReplaceSectionOpenByDefault(persistedIds: ReadonlySet<string>, selectedIds: ReadonlySet<string> | null) {
  return persistedIds.size > 0 || (selectedIds?.size ?? 0) > 0
}

/** Why a sales-order milestone can't be replaced by this change order, or null
 * when it can. Mirrors the server eligibility rule; the server stays the
 * authority (it also checks NetSuite billing and live payment links). */
export function getMilestoneReplacementBlocker(milestone: ReservableMilestone, quoteId: string): string | null {
  if (milestone.statuscode === PaymentMilestoneStatus.Paid) return REPLACEMENT_BLOCKER.paid
  if (milestone.dsy_paylink?.nodus_pfstatustext === PAYLINK_STATUS.Paid) return REPLACEMENT_BLOCKER.paid
  if (milestone._dsy_epayment_value) return REPLACEMENT_BLOCKER.paymentReceived
  if (milestone.statuscode === PaymentMilestoneStatus.WaitingForPayment) return REPLACEMENT_BLOCKER.waitingForPayment
  if (milestone.dsy_paylink) return REPLACEMENT_BLOCKER.paymentLinkSent
  if (milestone.dsy_invoiceissueddate || milestone.dsy_quickbooksinvoiceid) return REPLACEMENT_BLOCKER.invoiced
  // An invoice posted to QuickBooks but not stamped back leaves only the error.
  if (milestone.dsy_quickbookslastsyncerror?.trim()) return REPLACEMENT_BLOCKER.quickBooksReview
  if (milestone.statuscode === PaymentMilestoneStatus.ToBeInvoiced) return REPLACEMENT_BLOCKER.toBeInvoiced
  const replacement = getMilestoneReplacement(milestone, quoteId)
  if (replacement?.kind === 'other') return `Replaced by ${replacement.label}`
  // Already Inactive under this change order (e.g. a failed apply): the
  // server accepts keeping the reservation.
  if (replacement?.kind === 'self') return null
  if (isMilestoneInactive(milestone) || milestone.statuscode !== PaymentMilestoneStatus.Open) {
    return REPLACEMENT_BLOCKER.inactive
  }
  // A lookup whose quote didn't expand counts as held (fail closed).
  if (isHeldByAnotherQuote(milestone, quoteId)) {
    return `Reserved by ${getReplacingQuoteLabel(milestone.dsy_replacedbyquote)}`
  }
  return null
}
